import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner, Badge,
} from "react-bootstrap";
import { FaFileExcel } from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import { DEBT_MONTH_PRESETS, buildDebtMonthRange, debtCodeStyle } from "./utils/debtMonthFilter";
import { buildChips, countActiveChips, resolveMapLabel } from "./utils/accountingFilterUtils";
import { RECEIVABLE_SOURCE_MAP, formatDebtSourceLabels, debtSourceBadgeBg } from "./utils/accountingSourceLabels";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import DebtDetailModal from "./components/DebtDetailModal";
import { formatVND as fmtVND } from "./utils/moneyFormat";
import "./accountingFilters.css";
import "./accountingResponsive.css";
const PAGE_SIZE = 20;
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const DEFAULT_MONTH_PRESET = "0";
const EMPTY_RECEIVABLE_FILTERS = {
  monthPreset: DEFAULT_MONTH_PRESET,
  customerCode: "",
  status: "",
  type: "",
  dateFrom: buildDebtMonthRange(0).dateFrom,
  dateTo: buildDebtMonthRange(0).dateTo,
};
const STATUS_MAP = {
  OPEN: { label: "Còn nợ", bg: "warning" },
  PARTIAL: { label: "Thu một phần", bg: "info" },
  CLOSED: { label: "Đã thu đủ", bg: "success" },
  OVERDUE: { label: "Quá hạn", bg: "danger" },
};
const SOURCE_MAP = RECEIVABLE_SOURCE_MAP;
const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };
const agingColor = (days) => {
  if (days <= 0) return "";
  if (days <= 7) return "#198754";
  if (days <= 15) return "#ffc107";
  if (days <= 30) return "#fd7e14";
  return "#dc3545";
};
const buildParams = (filters) => {
  const params = {};
  if (filters.customerCode) params.customerCode = filters.customerCode;
  if (filters.status) params.status = filters.status;
  if (filters.type) params.type = filters.type;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  return params;
};
const buildFiltersFromPreset = (monthPreset, base = {}) => {
  const range = buildDebtMonthRange(monthPreset);
  return {
    ...base,
    monthPreset,
    dateFrom: range.dateFrom || "",
    dateTo: range.dateTo || "",
  };
};
const ReceivableList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [aging, setAging] = useState({
    totalItems: 0, overdue7Days: 0, overdue15Days: 0, overdue30Days: 0,
    overdue7Amount: 0, overdue15Amount: 0, overdue30Amount: 0,
  });
  const [filterCustomerCode, setFilterCustomerCode] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [monthPreset, setMonthPreset] = useState(DEFAULT_MONTH_PRESET);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_RECEIVABLE_FILTERS);
  const [totals, setTotals] = useState({ totalDebt: 0, totalReceived: 0, totalRemaining: 0, totalCount: 0 });
  const [detailModal, setDetailModal] = useState(null);
  const chipDefs = useMemo(() => [
    {
      key: "monthPreset",
      label: "Kỳ công nợ",
      isActive: (v) => v && v !== DEFAULT_MONTH_PRESET,
      formatValue: (v) => DEBT_MONTH_PRESETS.find((p) => p.value === v)?.label || v,
    },
    {
      key: "customerCode",
      label: "Đối tượng thu",
      isActive: (v) => Boolean(v),
    },
    {
      key: "status",
      label: "Trạng thái",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveMapLabel(v, STATUS_MAP),
    },
    {
      key: "type",
      label: "Nguồn",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveMapLabel(v, SOURCE_MAP),
    },
  ], []);
  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );
  const fetchAging = useCallback(async (filters = appliedFilters) => {
    try {
      const params = buildParams(filters);
      const [agingRes, totalsRes] = await Promise.all([
        axiosInstance.get("/api/accounting/receivables/aging", { params }),
        axiosInstance.get("/api/accounting/receivables/totals", { params }),
      ]);
      const agingData = unwrap(agingRes);
      const totalsData = unwrap(totalsRes);
      if (agingData) setAging(agingData);
      if (totalsData) setTotals(totalsData);
    } catch (_) { /* silent */ }
  }, [appliedFilters]);
  const fetchData = useCallback(async (p = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = { page: p, size: PAGE_SIZE, ...buildParams(filters) };
      const res = await axiosInstance.get("/api/accounting/receivables", { params });
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch (err) {
      toast.error("Không thể tải dữ liệu công nợ phải thu");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);
  useEffect(() => {
    fetchData(0, EMPTY_RECEIVABLE_FILTERS);
    fetchAging(EMPTY_RECEIVABLE_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const applyFilters = (next) => {
    setAppliedFilters(next);
    fetchData(0, next);
    fetchAging(next);
  };
  const handleSearch = () => {
    const next = buildFiltersFromPreset(monthPreset, {
      customerCode: filterCustomerCode.trim(),
      status: filterStatus,
      type: filterType,
    });
    applyFilters(next);
  };
  const syncDraftFromApplied = (filters) => {
    setFilterCustomerCode(filters.customerCode || "");
    setFilterStatus(filters.status || "");
    setFilterType(filters.type || "");
    setMonthPreset(filters.monthPreset || DEFAULT_MONTH_PRESET);
  };
  const handleReset = () => {
    syncDraftFromApplied(EMPTY_RECEIVABLE_FILTERS);
    applyFilters(EMPTY_RECEIVABLE_FILTERS);
  };
  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "monthPreset") {
      Object.assign(base, buildFiltersFromPreset(DEFAULT_MONTH_PRESET, {
        customerCode: base.customerCode,
        status: base.status,
        type: base.type,
      }));
    } else {
      base[key] = "";
    }
    syncDraftFromApplied(base);
    applyFilters(base);
  };
  const exportExcel = () => {
    if (!rows.length) { toast.warn("Không có dữ liệu để xuất"); return; }
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r, i) => ({
        STT: i + 1 + page * PAGE_SIZE,
        "Mã đối tượng thu": r.customerCode || "",
        "Tổng nợ": r.totalDebt || 0,
        "Đã thu": r.receivedAmount || 0,
        "Còn lại": r.remainingAmount || 0,
        "Tuổi nợ (ngày)": r.agingDays || 0,
        "Nguồn": formatDebtSourceLabels(r.sourceType, SOURCE_MAP),
        "Trạng thái": STATUS_MAP[r.status]?.label || r.status,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CongNoPhaiThu");
    XLSX.writeFile(wb, "CongNoPhaiThu.xlsx");
  };
  const totalRemaining = totals.totalRemaining || 0;
  const summaryCards = [
    { label: "Tổng còn phải thu", value: totalRemaining, color: "#0d6efd" },
    { label: "Quá hạn > 7 ngày", value: aging.overdue7Amount, color: "#198754" },
    { label: "Quá hạn > 15 ngày", value: aging.overdue15Amount, color: "#ffc107" },
    { label: "Quá hạn > 30 ngày", value: aging.overdue30Amount, color: "#dc3545" },
  ];
  const body = (
      <div className={embedded ? "" : "acc-page"}>
        {!embedded && <h5 className="fw-bold mb-3">Công nợ phải thu</h5>}
        <div className="acc-summary-grid--equal">
          {summaryCards.map((c, i) => (
            <div className="acc-summary-card-wrap" key={i}>
              <Card className="h-100" style={{ ...cardStyle, borderLeft: `4px solid ${c.color}` }}>
                <Card.Body className="py-2 px-2">
                  <div className="acc-kpi-title text-truncate" title={c.label}>{c.label}</div>
                  <div className="acc-kpi-value text-truncate" style={{ color: c.color }}>{fmtVND(c.value)}</div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
        <Card className="mb-3 acc-filter-card">
          <Card.Body className="p-0">
            <AccountingFilterSection
              open={filterOpen}
              onToggle={setFilterOpen}
              onSubmit={handleSearch}
              onReset={handleReset}
              activeCount={countActiveChips(activeChips)}
              toolbarRight={(
                <Button variant="success" size="sm" onClick={exportExcel}>
                  <FaFileExcel className="me-1" />Excel
                </Button>
              )}
            >
              <Row className="g-2 align-items-end acc-filter-grid">
                <Col>
                  <Form.Label className="small mb-1">Kỳ công nợ</Form.Label>
                  <Form.Select size="sm" value={monthPreset} onChange={(e) => setMonthPreset(e.target.value)}>
                    {DEBT_MONTH_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Mã đối tượng thu</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Nhập mã đối tượng thu..."
                    value={filterCustomerCode}
                    onChange={(e) => setFilterCustomerCode(e.target.value)}
                  />
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Trạng thái</Form.Label>
                  <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">Tất cả</option>
                    {Object.entries(STATUS_MAP).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Nguồn</Form.Label>
                  <Form.Select size="sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="">Tất cả</option>
                    {Object.entries(SOURCE_MAP).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </AccountingFilterSection>
            <AccountingActiveFilters
              chips={activeChips}
              onRemove={handleRemoveChip}
              onClearAll={handleReset}
            />
          </Card.Body>
        </Card>
        <Card style={cardStyle}>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : rows.length === 0 ? (
              <div className="text-center py-5 text-muted">Không có dữ liệu công nợ phải thu</div>
            ) : (
              <>
                <div className="acc-table-wrap">
                  <Table striped hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Đối tượng thu</th>
                        <th className="text-end">Tổng nợ</th>
                        <th className="text-end">Đã thu</th>
                        <th className="text-end">Còn lại</th>
                        <th className="text-center">Tuổi nợ</th>
                        <th className="text-center acc-col-hide-sm">Nguồn</th>
                        <th className="text-center">Trạng thái</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr
                          key={r.partnerId || r.customerId || idx}
                          style={{ cursor: "pointer" }}
                          onClick={() => setDetailModal({
                            type: "receivable",
                            entityId: r.partnerId || r.customerId,
                            entityCode: r.customerCode,
                          })}
                        >
                          <td className="fw-semibold text-muted" style={debtCodeStyle(r.warningLevel)}>{r.customerCode || "—"}</td>
                          <td className="text-end">{fmtVND(r.totalDebt)}</td>
                          <td className="text-end">{fmtVND(r.receivedAmount)}</td>
                          <td className="text-end fw-semibold">{fmtVND(r.remainingAmount)}</td>
                          <td className="text-center">
                            <span style={{
                              color: "#fff",
                              backgroundColor: agingColor(r.agingDays) || "#6c757d",
                              borderRadius: 4, padding: "2px 8px", fontSize: "0.75rem",
                            }}>
                              {r.agingDays || 0} ngày
                            </span>
                          </td>
                          <td className="text-center acc-col-hide-sm">
                            <Badge bg={debtSourceBadgeBg(r.sourceType, SOURCE_MAP)} className="me-1">
                              {formatDebtSourceLabels(r.sourceType, SOURCE_MAP) || "N/A"}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg={STATUS_MAP[r.status]?.bg || "secondary"}>
                              {STATUS_MAP[r.status]?.label || r.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="acc-table-footer">
                  <div className="d-flex flex-wrap align-items-center gap-3 w-100 justify-content-between">
                    <small className="text-muted">Tổng: {totals.totalCount ?? totalElements} bản ghi</small>
                    <small className="fw-semibold">
                      Tổng nợ: {fmtVND(totals.totalDebt)}
                      <span className="text-muted fw-normal mx-2">|</span>
                      Đã thu: {fmtVND(totals.totalReceived)}
                      <span className="text-muted fw-normal mx-2">|</span>
                      Còn lại: <span className="text-primary">{fmtVND(totals.totalRemaining)}</span>
                    </small>
                  </div>
                  <Pagination
                    hidePaginationWhenSinglePage
                    showItemsPerPage={false}
                    pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                    onPageChange={(p) => fetchData(p - 1)}
                  />
                </div>
              </>
            )}
          </Card.Body>
        </Card>
        <DebtDetailModal
          show={!!detailModal}
          onHide={() => setDetailModal(null)}
          type={detailModal?.type}
          entityId={detailModal?.entityId}
          entityCode={detailModal?.entityCode}
        />
      </div>
  );
  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};
export default ReceivableList;
